// REACT
import { useEffect, useRef, useState, useCallback } from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";

// REDUX
import { useAppDispatch } from "src/store/store";
import {
  setCryptoWithdrawalStage,
  setNotabeneTxId,
  setNotabeneStatus,
  setNotabenePolicies,
  setNotabeneRequiresPii,
  setNotabenePiiSubmitted,
  resetNotabeneState,
  clearWithdrawalFormInput,
  useWithdrawalSlice,
} from "src/store/features/withdrawal.slice";

// API
import {
  useCreateNotabeneTransferMutation,
  useLazyGetNotabeneTransferQuery,
  useCompleteNotabeneTransferMutation,
  useCancelNotabeneTransferMutation,
} from "src/store/features/api/notabene";

// TYPES
import { Token } from "./CryptoWithdrawalModal";

// COMPONENTS
import NotabeneHostedMount, {
  NotabeneHostedMountResult,
} from "./NotabeneHostedMount";

// STYLES
import {
  StyledExplainerTextTypography,
  StyledNextButton,
} from "../common/styles/CryptoWithdrawalStyled";

// UTILS
// AMPLITUDE
import { trackEvent } from "src/Amplitude";
import { getSessionItem } from "src/utils/SessionManagement";

// ICONS
import { ErrorOutlineOutlined, CheckCircleOutline } from "@mui/icons-material";

interface ICryptoWithdrawalNotabeneProps {
  token: Token | null;
  withdrawalAddress: string | null;
  withdrawalAmount: number;
  onCompletion: (notabeneTxId: string) => void;
}

type ComponentState =
  | "initializing"
  | "creating_transfer"
  | "checking_status"
  | "authorized"
  | "requires_pii"
  | "pending"
  | "manual_review"
  | "error";

/**
 * CryptoWithdrawalNotabene component
 * Integrates Notabene for Travel Rule compliance
 *
 * Flow:
 * 1. Create transfer via backend API
 * 2. If immediately authorized, proceed to Review
 * 3. If requires PII, mount the shared <NotabeneHostedMount kind="withdrawal-assist" />
 * 4. If pending, poll for status updates
 *
 * The SafeConnect SDK mount block was previously inlined here. It now lives
 * in `<NotabeneHostedMount />` and is shared with D3 ownership-attestation,
 * D18 info-request and the D3 Exchange branch's vasp-picker.
 */
export const CryptoWithdrawalNotabene = ({
  token,
  withdrawalAddress,
  withdrawalAmount,
  onCompletion,
}: ICryptoWithdrawalNotabeneProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const {
    notabeneTxId: persistedTxId,
    notabenePiiSubmitted,
    withdrawalPreparedToken,
  } = useWithdrawalSlice();
  const [componentState, setComponentState] = useState<ComponentState>(
    notabenePiiSubmitted ? "pending" : "initializing",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [txId, setTxId] = useState<string | null>(persistedTxId);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  // Cap status polling so a transfer that never reaches a terminal status can't
  // poll the backend forever. 40 × 3s ≈ 2 minutes; after that the awaiting
  // banner stays up (it already tells the user they can close and come back).
  const MAX_POLL_ATTEMPTS = 40;
  const pollAttemptsRef = useRef(0);
  // Consecutive 404s from the status poll. A 404 means this PROFILE cannot
  // see the transfer (ownership guard) — typically the user switched account
  // profiles mid-flight, or the record was purged. Retrying can never
  // succeed, so after a few confirming attempts we stop polling and park the
  // flow instead of hammering the backend every 3s (the 2026-07-13
  // /notabene/transfer 4xx-rate alert was exactly this loop).
  const consecutive404Ref = useRef(0);
  const MAX_CONSECUTIVE_404S = 3;
  // Set when the poll parks on confirmed 404s. The resets that clear the
  // persisted TR state + form fields would re-trigger the mount effect and
  // bounce the stage to Verification — unmounting this component before the
  // profile-mismatch error is ever seen. The mount effect bails while this
  // is set, and the error screen hides "Try Again" (the transfer is foreign
  // or gone, and the cleared form leaves nothing to retry with). State, not
  // a ref, so the render reacts to it.
  const [parkedOn404, setParkedOn404] = useState(false);
  const currentEmail = getSessionItem("email");
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // API hooks
  const [createTransfer, { isLoading: isCreatingTransfer }] = useCreateNotabeneTransferMutation();
  const [getTransferStatus] = useLazyGetNotabeneTransferQuery();
  const [completeTransfer] = useCompleteNotabeneTransferMutation();
  const [cancelTransfer, { isLoading: isCancelling }] = useCancelNotabeneTransferMutation();

  const handleGoBack = () => {
    dispatch(setNotabeneTxId(null));
    dispatch(setNotabenePiiSubmitted(false));
    dispatch(setCryptoWithdrawalStage("InputDetails"));
  };

  // Cancel an in-flight transfer from the manual-review screen. This marks the
  // transfer CANCELED on the backend (authoritative — the webhook path now
  // refuses to regress a CANCELED row back to AUTHORIZED, so a later compliance
  // authorize in Notabene can't silently dispatch this abandoned withdrawal).
  // We then fully reset the local flow back to the start.
  const handleCancelWithdrawal = useCallback(async () => {
    if (txId) {
      try {
        await cancelTransfer(txId).unwrap();
        trackEvent(
          "Notabene Transfer Cancelled",
          { txId, email: currentEmail },
          "Crypto Withdrawal Modal",
        );
      } catch (error: any) {
        // Best-effort: even if the cancel call fails we still tear down the
        // local flow. The resume banner's status filter will re-surface the
        // transfer if the backend didn't record the cancel, so the user isn't
        // stranded.
        console.error("Error cancelling Notabene transfer:", error);
        trackEvent(
          "Notabene Transfer Cancel Error",
          { txId, error: error?.message, email: currentEmail },
          "Crypto Withdrawal Modal",
        );
      }
    }
    dispatch(resetNotabeneState());
    dispatch(setCryptoWithdrawalStage("InputDetails"));
  }, [txId, cancelTransfer, currentEmail, dispatch]);

  const handleRetry = () => {
    setParkedOn404(false);
    setRetryCount((prev) => prev + 1);
    setErrorMessage(null);
    setTxId(null);
    setComponentState("initializing");
    dispatch(setNotabeneTxId(null));
    dispatch(setNotabenePiiSubmitted(false));
  };

  const handleProceedToReview = useCallback(() => {
    if (txId) {
      dispatch(setNotabeneTxId(txId));
      dispatch(setNotabeneStatus("authorized"));
      onCompletion(txId);
    }
  }, [txId, dispatch, onCompletion]);

  const checkTransferStatus = useCallback(
    async (transferTxId: string) => {
      try {
        const result = await getTransferStatus(transferTxId).unwrap();
        consecutive404Ref.current = 0;
        const status = result.status.toUpperCase();

        dispatch(setNotabeneStatus(status.toLowerCase() as any));
        if (result.policies) {
          dispatch(setNotabenePolicies(result.policies as any));
        }

        if (
          ["AUTHORIZED", "ACK", "SETTLED", "TR_AUTHORIZED", "FIREBLOCKS_DISPATCHED"].includes(
            status,
          )
        ) {
          setComponentState("authorized");
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          return "authorized";
        } else if (
          ["REJECTED", "CANCELLED", "CANCELED", "DENIED", "TR_DECLINED"].includes(status)
        ) {
          // Terminal decline ALWAYS wins — evaluated before the requires_pii /
          // requiresPresentation branch below. A declined transfer can't be
          // rescued by presenting PII, so a stale requiresPresentation flag must
          // not route the user back into the SafeConnect SDK. This is reachable:
          // a decline that arrives as a `transferStatusChanged` (rather than via
          // the decision handler) doesn't clear requiresPresentation, and
          // REJECTED is in the refetch-skip set so getTransfer returns the
          // cached flag (Bugbot: "Declined status loses to flag").
          setComponentState("error");
          setErrorMessage("Transfer was rejected. Please contact support.");
          return "rejected";
        } else if (
          status === "REQUIRES_PII" ||
          status === "TR_INFO_REQUESTED" ||
          // Durable backend signal that the originator still owes IVMS101 PII.
          // Checked here — BEFORE the FLAGGED/manual_review branch below — so an
          // outstanding presentation always wins over a transient FLAGGED and
          // the SafeConnect SDK stays mounted (prod incident 2026-06-24, where
          // FLAGGED landed 0.3s after the presentation request and hid the SDK).
          result.requiresPresentation === true ||
          result.policies?.some(
            (p: any) => p.type === "RequirePresentation" && p.status !== "satisfied",
          )
        ) {
          setComponentState("requires_pii");
          dispatch(setNotabeneRequiresPii(true));
          return "requires_pii";
        } else if (
          [
            "PENDING",
            "WAITING",
            "SENT",
            "TR_AWAITING_DECISION",
            "AWAITING_AUTHORIZATION",
            // Documented Notabene v2 "counterparty review" status.
            "PROCESSING",
          ].includes(status)
        ) {
          setComponentState("pending");
          return "pending";
        } else if (
          ["FLAGGED", "MANUAL_REVIEW", "TR_MANUAL_REVIEW", "REVERT-REQUESTED"].includes(status)
        ) {
          // Notabene flagged the transfer for compliance review (or our
          // orchestrator escalated it to manual review). This is NOT an error
          // and NOT terminal — an operator/counterparty can still release it,
          // at which point a later poll observes AUTHORIZED and self-heals.
          // Without this branch FLAGGED fell through to the UNKNOWN→error case
          // below and showed the misleading "We couldn't confirm the status"
          // red error screen (issue #3378). Surface the dedicated manual-review
          // waiting screen instead, and DON'T clear the poll interval so the
          // UI keeps watching for the release.
          //
          // BUT only once the user has actually submitted their PII. Before
          // submission a transient FLAGGED must not pre-empt the SafeConnect SDK
          // — the user may still owe a presentation (the requires_pii branch
          // above catches the durable signal, but if it isn't set yet we must
          // not strand them on the manual-review screen). Fall through to
          // requires_pii so the SDK mounts (prod incident 2026-06-24).
          if (!notabenePiiSubmitted) {
            setComponentState("requires_pii");
            dispatch(setNotabeneRequiresPii(true));
            return "requires_pii";
          }
          setComponentState("manual_review");
          return "manual_review";
        }

        // Any other / UNKNOWN or unrecognized status. The backend coerces
        // statuses it cannot map to "UNKNOWN". Before the user has submitted
        // PII, the SafeConnect SDK is their path forward — show it rather than a
        // dead-end error screen (the SDK surfaces whatever, if anything, Notabene
        // still needs). Only after submission do we treat an unmappable status as
        // a genuine stuck state worth the error + support reference.
        if (!notabenePiiSubmitted) {
          setComponentState("requires_pii");
          dispatch(setNotabeneRequiresPii(true));
          return "requires_pii";
        }
        // If we leave componentState untouched the caller stays on the perpetual
        // "Creating travel rule transfer…" spinner. Resolve to a deterministic,
        // actionable state that surfaces the reference, and stop any polling.
        setComponentState("error");
        setErrorMessage(
          `We couldn't confirm the status of your travel rule transfer (reference: ${transferTxId}). ` +
            "It may be under manual review — please contact support with this reference if it doesn't resolve shortly.",
        );
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return "unknown";
      } catch (error: any) {
        console.error("Error checking transfer status:", error);
        // 404 = the active profile doesn't own this transfer (or it's gone).
        // Not transient — after MAX_CONSECUTIVE_404S confirmations, stop the
        // poll loop, clear the persisted TR state (so the banner doesn't
        // re-enter the loop on the next mount), and surface an actionable
        // message. Other errors (network blips, 5xx) keep the retry cadence.
        if (error?.status === 404) {
          consecutive404Ref.current += 1;
          // The 3-strike threshold only makes sense while the poll loop is
          // running to deliver strikes 2 and 3. One-shot checks — the mount
          // resume before PII is submitted, or the post-create check — have
          // no retry behind them, so waiting for 3 confirmations just
          // strands the spinner forever; park on the first 404 there.
          const isOneShotCheck = pollIntervalRef.current === null;
          if (isOneShotCheck || consecutive404Ref.current >= MAX_CONSECUTIVE_404S) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setParkedOn404(true);
            dispatch(resetNotabeneState());
            // The form fields (token, destination, amount) are as
            // profile-scoped as the transfer itself — after a cross-tab
            // profile switch they belong to the OTHER profile, and leaving
            // them would prefill this profile's next withdrawal with that
            // destination. Cleared separately from the notabene reset so the
            // stage + modal (and this error screen) stay mounted.
            dispatch(clearWithdrawalFormInput());
            setComponentState("error");
            setErrorMessage(
              "We can't find this travel rule transfer under the account profile you're currently using. " +
                "If you started this withdrawal under a different profile, switch back to that profile to continue it — " +
                "otherwise please start the withdrawal again.",
            );
            return "not_found";
          }
        }
        return "error";
      }
    },
    [getTransferStatus, dispatch, notabenePiiSubmitted],
  );

  // Create transfer when component mounts. Resume existing one if redux holds a txId.
  useEffect(() => {
    // Parked on confirmed 404s — the state resets that parked us re-fire this
    // effect with no persisted txId, no prepared token, and no form fields,
    // which would otherwise bounce the stage to Verification and unmount the
    // error UI. Stay put until the user acts (Go Back).
    if (parkedOn404) {
      return;
    }
    if (!token || !withdrawalAddress || !withdrawalAmount) {
      return;
    }

    if (persistedTxId) {
      setTxId(persistedTxId);
      checkTransferStatus(persistedTxId);
      return;
    }

    if (!withdrawalPreparedToken) {
      dispatch(setCryptoWithdrawalStage("Verification"));
      return;
    }

    // Set by the cleanup when this effect run is superseded — the component
    // unmounted, or a dep changed (a profile switch dispatches
    // resetWithdrawalState, which nulls the token/prepared-token deps). An
    // in-flight createTransfer resolving after that must NOT write its txId
    // back into the freshly-cleared slice: the hydrate hook has already
    // marked the new profile hydrated, so nothing would ever clear the
    // foreign txId again and the wrong-profile banner would return.
    let cancelled = false;

    const initiateTransfer = async () => {
      setComponentState("creating_transfer");

      try {
        trackEvent(
          "Notabene Transfer Creating",
          { token: token.code, email: currentEmail },
          "Crypto Withdrawal Modal",
        );

        const result = await createTransfer({
          asset: token.code,
          amount: withdrawalAmount.toString(),
          destinationAddress: withdrawalAddress,
          withdrawalPreparedToken: withdrawalPreparedToken ?? undefined,
        }).unwrap();

        if (cancelled) {
          return;
        }

        setTxId(result.txId);
        dispatch(setNotabeneTxId(result.txId));

        trackEvent(
          "Notabene Transfer Created",
          { token: token.code, txId: result.txId, status: result.status, email: currentEmail },
          "Crypto Withdrawal Modal",
        );

        await checkTransferStatus(result.txId);
      } catch (error: any) {
        console.error("Error creating Notabene transfer:", error);
        trackEvent(
          "Notabene Transfer Creation Error",
          { token: token.code, error: error?.message, email: currentEmail },
          "Crypto Withdrawal Modal",
        );
        if (cancelled) {
          return;
        }
        setComponentState("error");
        setErrorMessage(
          error?.data?.message || "Failed to create travel rule transfer. Please try again.",
        );
      }
    };

    initiateTransfer();

    return () => {
      cancelled = true;
    };
  }, [
    token,
    withdrawalAddress,
    withdrawalAmount,
    createTransfer,
    dispatch,
    checkTransferStatus,
    currentEmail,
    retryCount,
    persistedTxId,
    withdrawalPreparedToken,
    parkedOn404,
  ]);

  // Reset the poll-attempt counter only when a genuinely new poll session
  // begins (a new transfer, or a fresh PII submission). Keeping this out of the
  // polling effect below is important: that effect also re-runs on every
  // `componentState` change, and resetting the counter there would let a
  // transfer that flips between non-terminal states (e.g. pending ⇄
  // requires_pii) poll forever, defeating the MAX_POLL_ATTEMPTS cap.
  useEffect(() => {
    pollAttemptsRef.current = 0;
  }, [txId, notabenePiiSubmitted]);

  // Poll for authorization once PII has been submitted.
  useEffect(() => {
    if (
      !txId ||
      !notabenePiiSubmitted ||
      componentState === "authorized" ||
      componentState === "error"
    ) {
      return;
    }

    pollIntervalRef.current = setInterval(() => {
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return;
      }
      checkTransferStatus(txId);
    }, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [txId, notabenePiiSubmitted, componentState, checkTransferStatus]);

  // Handle SafeConnect completion via the shared NotabeneHostedMount.
  const handleHostedComplete = useCallback(
    async (data: NotabeneHostedMountResult) => {
      const finalTxId = data.reference || txId;
      if (!finalTxId) return;
      try {
        // Proof-only completions (confirmRelationship without ivms101) must
        // still reach the backend — the wallet-ownership proof is submitted
        // upstream via Notabene's unhosted-proof PATCH. The backend skips
        // /append when ivms101 is absent and still proofs + authorizes.
        if (data.ivms101 || data.confirmRelationship) {
          await completeTransfer({
            txId: finalTxId,
            ivms101: data.ivms101,
            confirmRelationship: data.confirmRelationship,
            withdrawalPreparedToken: withdrawalPreparedToken ?? undefined,
          }).unwrap();
        }
        dispatch(setNotabenePiiSubmitted(true));
        const post = await checkTransferStatus(finalTxId);
        if (post === "authorized") {
          handleProceedToReview();
        }
      } catch (err: any) {
        console.error("Failed to append/authorize Notabene transfer:", err);
        setComponentState("error");
        setErrorMessage(
          err?.data?.message ||
            "Failed to complete travel rule verification. Please try again.",
        );
      }
    },
    [
      txId,
      completeTransfer,
      withdrawalPreparedToken,
      dispatch,
      checkTransferStatus,
      handleProceedToReview,
    ],
  );

  // Show loading while creating transfer
  if (
    componentState === "initializing" ||
    componentState === "creating_transfer" ||
    isCreatingTransfer
  ) {
    return (
      <Grid container direction="column" py={1} pb={4}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          py={6}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2, color: "#667085" }}>
            Creating travel rule transfer…
          </Typography>
        </Box>
      </Grid>
    );
  }

  // Show manual-review state. FLAGGED / MANUAL_REVIEW is a pending compliance
  // hold, not a failure — mirror the "waiting for counterparty" surface (neutral,
  // not the red error screen) but word it for manual review. Polling stays live,
  // so if compliance releases the transfer a later poll flips this to
  // "authorized" and the authorized banner with "Continue to Review" appears.
  if (componentState === "manual_review") {
    return (
      <Grid container direction="column" py={1} pb={4}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={6}
          px={3}
          sx={{
            backgroundColor: "#F9FAFB",
            borderRadius: "8px",
            border: "1px solid #EAECF0",
          }}>
          <CircularProgress size={32} sx={{ mb: 2 }} />
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#344054",
              mb: 1,
              textAlign: "center",
            }}>
            Withdrawal under manual review
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#667085",
              textAlign: "center",
              maxWidth: "440px",
              mb: 3,
            }}>
            Your travel rule verification needs a manual compliance review. This can take a
            little while — you can close this window and come back to complete your withdrawal
            once the review has finished, or cancel this withdrawal.
          </Typography>
          <StyledNextButton
            onClick={handleCancelWithdrawal}
            disabled={isCancelling}
            sx={{
              maxWidth: "200px",
              backgroundColor: "transparent",
              color: "#B42318",
              border: "1px solid #FDA29B",
              "&:hover": { backgroundColor: "#FEF3F2" },
            }}>
            {isCancelling ? "Cancelling…" : "Cancel withdrawal"}
          </StyledNextButton>
        </Box>
      </Grid>
    );
  }

  // Show error state
  if (componentState === "error") {
    return (
      <Grid container direction="column" py={1} pb={4}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={6}
          px={2}
          sx={{
            backgroundColor: "#FEF3F2",
            borderRadius: "8px",
            border: "1px solid #FEE4E2",
          }}>
          <ErrorOutlineOutlined sx={{ fontSize: 48, color: "#D92D20", mb: 2 }} />
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#B42318",
              mb: 1,
              textAlign: "center",
            }}>
            Unable to complete verification
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#7A271A", textAlign: "center", mb: 3 }}>
            {errorMessage ||
              "We encountered an issue with travel rule verification. Please try again later."}
          </Typography>
          <Box display="flex" gap={2}>
            {/* No retry when parked on confirmed 404s: the transfer belongs
                to another profile (or is gone) and the form fields were
                cleared, so a retry has nothing to resume or re-create. */}
            {retryCount < MAX_RETRIES && !parkedOn404 && (
              <StyledNextButton onClick={handleRetry} sx={{ maxWidth: "200px" }}>
                Try Again
              </StyledNextButton>
            )}
            <StyledNextButton
              onClick={handleGoBack}
              sx={{
                maxWidth: "200px",
                ...(retryCount < MAX_RETRIES &&
                  !parkedOn404 && {
                    backgroundColor: "transparent",
                    color: "#344054",
                    border: "1px solid #D0D5DD",
                    "&:hover": { backgroundColor: "#F9FAFB" },
                  }),
              }}>
              Go Back
            </StyledNextButton>
          </Box>
        </Box>
      </Grid>
    );
  }

  // Default render — surface depends on PII-submitted state.
  const showAuthorizedBanner = componentState === "authorized";
  const showAwaitingBanner = notabenePiiSubmitted && !showAuthorizedBanner;
  // The SDK is the user's path to submitting PII, so it stays up until they
  // have — UNLESS the transfer is already authorized. Notabene can authorize
  // at tx-create (its own policy passes a low-value transfer with no
  // presentation owed), in which case `notabenePiiSubmitted` is still false
  // and, without this guard, the "Continue to Review" banner and the
  // SafeConnect iframe rendered together. Authorized is terminal-positive:
  // there is nothing left for the SDK to collect, so only the banner shows.
  const showSdk = !notabenePiiSubmitted && !showAuthorizedBanner;

  return (
    <Grid container direction="column" py={1} pb={4}>
      <StyledExplainerTextTypography>
        To comply with travel rule regulations, we need to verify your withdrawal recipient.
      </StyledExplainerTextTypography>

      {showAuthorizedBanner && (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          py={2}
          px={3}
          mb={2}
          sx={{
            backgroundColor: "#ECFDF3",
            borderRadius: "8px",
            border: "1px solid #ABEFC6",
          }}>
          <Box display="flex" alignItems="center">
            <CheckCircleOutline sx={{ fontSize: 28, color: "#039855", mr: 1.5 }} />
            <Box>
              <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#054F31" }}>
                Travel Rule Verified
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#067647" }}>
                Your withdrawal has been approved for travel rule compliance.
              </Typography>
            </Box>
          </Box>
          <StyledNextButton onClick={handleProceedToReview} sx={{ maxWidth: "180px" }}>
            Continue to Review
          </StyledNextButton>
        </Box>
      )}

      {showAwaitingBanner && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={6}
          px={3}
          sx={{
            backgroundColor: "#F9FAFB",
            borderRadius: "8px",
            border: "1px solid #EAECF0",
          }}>
          <CircularProgress size={32} sx={{ mb: 2 }} />
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#344054",
              mb: 1,
              textAlign: "center",
            }}>
            Waiting for counterparty verification…
          </Typography>
          <Typography
            sx={{ fontSize: "13px", color: "#667085", textAlign: "center", maxWidth: "440px" }}>
            We've submitted your verification details. This can take a little while — you can
            close this window and come back to complete your withdrawal once the check has
            finished.
          </Typography>
        </Box>
      )}

      {showSdk && txId && (
        <NotabeneHostedMount
          kind="withdrawal-assist"
          txId={txId}
          asset={token?.code}
          destinationAddress={withdrawalAddress ?? undefined}
          amount={withdrawalAmount}
          // Enable Counterparty Assist on every outbound mount. This unlocks
          // three flows we'd otherwise be blocked on:
          //   - SELF: cross-device wallet signing (mobile signs while
          //           customer started on desktop)
          //   - NATURAL/LEGAL: third-party recipients fill in their own
          //                    PII via a shared link (Bob → Sue's
          //                    accountant flow)
          //   - SELF/NATURAL/LEGAL: D18 info_requested recovery on
          //                         re-mount with the same txId — the
          //                         SDK surfaces the missing fields
          // Each emits a `refreshSource` we resolve via the SDK's
          // `getRefreshResult` helper inside NotabeneHostedMount.
          counterpartyAssist
          onComplete={handleHostedComplete}
          redirectFallbackUrl={
            txId
              ? `${window.location.origin}/transfers/${txId}?notabene_callback=1`
              : null
          }
        />
      )}
    </Grid>
  );
};
